import { useState } from 'react';
import type { CatalogItem } from '../api';
import { Badge, Button } from '../components/ui';
import { findItem, isSelected, setManyItems, toggleItem, updateItem, type Draft } from './draft';

/**
 * What they had, and what to take away.
 *
 * Everything selected defaults to revoke, because that is the safe direction:
 * an access left on by accident outlives the person, one taken away by mistake
 * is a phone call. Keeping something is the deliberate act, and it demands a
 * reason in writing.
 */
export function StepRevoke({
  groups,
  draft,
  onChange,
}: {
  groups: { key: string; label: string; entries: CatalogItem[] }[];
  draft: Draft;
  onChange: (next: Draft) => void;
}) {
  const [filter, setFilter] = useState('');
  const needle = filter.trim().toLowerCase();

  const all = groups.flatMap((group) => group.entries);
  const selectedCount = all.filter((entry) => isSelected(draft, entry.id)).length;
  const keptCount = all.filter((entry) => findItem(draft, entry.id)?.revoke === false).length;

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">Access to revoke</h2>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-600 dark:text-slate-400">
            Tick everything this person had. All of it is revoked unless you say otherwise; keeping something
            needs a reason, because it outlives them if nobody notices.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={selectedCount > 0 ? 'brand' : 'neutral'}>{selectedCount} listed</Badge>
          {keptCount > 0 && <Badge tone="warning">{keptCount} kept</Badge>}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter…"
          aria-label="Filter access items"
          className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 sm:max-w-xs dark:border-slate-700 dark:bg-slate-900 dark:text-white"
        />
        <Button size="sm" variant="secondary" onClick={() => onChange(setManyItems(draft, all, true))}>
          Select everything
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onChange(setManyItems(draft, all, false))}>
          Clear
        </Button>
      </div>

      <div className="space-y-6">
        {groups.map((group) => {
          const entries = needle
            ? group.entries.filter((entry) => entry.name.toLowerCase().includes(needle))
            : group.entries;
          if (entries.length === 0) return null;

          return (
            <section key={group.key}>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
                  {group.label}
                </h3>
                <button
                  type="button"
                  onClick={() => onChange(setManyItems(draft, entries, true))}
                  className="text-xs font-medium text-brand-700 underline-offset-4 hover:underline dark:text-brand-300"
                >
                  Select all
                </button>
              </div>

              <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
                {entries.map((entry) => {
                  const on = isSelected(draft, entry.id);
                  const item = findItem(draft, entry.id);
                  const keep = item?.revoke === false;

                  return (
                    <li key={entry.id} className="px-4 py-2.5">
                      <div className="flex flex-wrap items-center gap-3">
                        <label className="flex min-w-0 flex-1 items-center gap-3">
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={(event) => onChange(toggleItem(draft, entry, event.target.checked))}
                            className="h-4 w-4 shrink-0 rounded border-slate-300 text-brand-600 dark:border-slate-600"
                          />
                          <span className="min-w-0 truncate text-sm text-slate-900 dark:text-slate-100">
                            {entry.name}
                          </span>
                        </label>

                        {on && (
                          <select
                            aria-label={`What to do with ${entry.name}`}
                            value={keep ? 'keep' : 'revoke'}
                            onChange={(event) =>
                              onChange(updateItem(draft, entry.id, { revoke: event.target.value === 'revoke' }))
                            }
                            className={`shrink-0 rounded-lg border px-2 py-1 text-xs ${
                              keep
                                ? 'border-copper-300 bg-copper-50 text-copper-800 dark:border-copper-800 dark:bg-copper-950/40 dark:text-copper-200'
                                : 'border-slate-300 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
                            }`}
                          >
                            <option value="revoke">Revoke</option>
                            <option value="keep">Keep for now</option>
                          </select>
                        )}
                      </div>

                      {on && keep && (
                        <input
                          value={item?.notes ?? ''}
                          onChange={(event) => onChange(updateItem(draft, entry.id, { notes: event.target.value }))}
                          placeholder="Why, and until when: this goes on the signed record"
                          aria-label={`Reason for keeping ${entry.name}`}
                          className="mt-2 w-full rounded-lg border border-copper-300 bg-white px-3 py-1.5 text-sm text-slate-900 dark:border-copper-800 dark:bg-slate-900 dark:text-white"
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}
